/* globals tv */
import '../../lib/freewheel-ad-manager';
import FREEWHEEL_DEFAULTS from '../defaults/Freewheel';
import isMobile from 'ismobilejs';
import assign from 'lodash/assign';
import queryString from 'query-string';
import md5 from 'md5';
import { hasUserConsentLib, inUserConsentState, isCCPA } from '../../utils/UserConsent';


class Freewheel {
    constructor (player = null, config = {}) {
        this.config = this.getConfig(config);
        this.siteSectionID = this.getSiteSectionID();
        this.theContext = null;
        this.prerolls = [];
        this.midrolls = [];
        this.postrolls = [];
        this.currentPlayingSlot = null;
        this.currentPlayingSlotType = null;
        this.showPreroll = this.config.preroll;
        this.showMidroll = this.config.midroll;
        this.showPostroll = this.config.postroll;
        this.mvpd = null;

        this.ASPlayer = player;
        this.asPlayerWrapperID = this.ASPlayer.el().getAttribute('id');

        this.theAdManager = new tv.freewheel.SDK.AdManager();
        this.theAdManager.setNetwork(this.config.networkID);
        this.theAdManager.setServer(this.config.serverURL);
        if (this.config.debug) tv.freewheel.SDK.setLogLevel(tv.freewheel.SDK.LOG_LEVEL_DEBUG);
        else tv.freewheel.SDK.setLogLevel(tv.freewheel.SDK.LOG_LEVEL_QUIET);

        this.configureShortcuts();
    }


    getConfig (config) {
        const qs = queryString.parse(location.search);
        let qsConfig = {};
        if (qs.adServerRootUrl && qs.adServerRootUrl === 'dev') qsConfig.serverURL = '//bea4.v.fwmrm.net/ad/g/1'; // set dev server url if dev flag is set in query string
        return assign({}, FREEWHEEL_DEFAULTS, config, qsConfig);
    }


    getSiteSectionID (videoData = {}) {
        if (window.AS_FW_SSID) return window.AS_FW_SSID;

        let prefix = (videoData.auth === true) ? this.config.siteSectionAuthIDPrefix : this.config.siteSectionUnauthIDPrefix;
        let platform = (isMobile.any ? (isMobile.apple.device ? 'ios' : (isMobile.android.device ? 'android' : 'other')) : 'desktop');
        return `${prefix}-${platform}`;
    }


    getFreewheelVideoID (videoData) {
        if (!videoData) return FREEWHEEL_DEFAULTS.videoAssetID;
        if (videoData.titleId) {
            return FREEWHEEL_DEFAULTS.fwVideoIDPrefix + videoData.titleId;
        } else {
            return FREEWHEEL_DEFAULTS.fwVideoIDPrefix + videoData.id;
        }
    }


    setContext (videoData, events = {}) {
        var videoAssetID = this.getFreewheelVideoID(videoData);  // gets the freewheel id and sets it as the videoAssetID
        var videoDuration = (videoData.duration) ? videoData.duration : 86400; // gets the duration from the current video and passes it to the FW context

        if (this.theContext) {
            this.theContext.dispose();
            this.theContext = null;
        }

        this.theContext = this.theAdManager.newContext();
        this.theContext.setProfile(this.config.profileID);
        this.theContext.setVideoAsset(videoAssetID, videoDuration, this.config.networkID);

        // set new site section
        this.siteSectionID = this.getSiteSectionID(videoData);
        this.theContext.setSiteSection(this.siteSectionID, this.config.serverURL);

        this.theContext.registerVideoDisplayBase(this.asPlayerWrapperID);

        // mvpd and auth state for freewheel
        this.theContext.addKeyValue('_fw_ae', this.mvpd || 'd41d8cd98f00b204e9800998ecf8427e');
        this.theContext.addKeyValue('_fw_ar', (videoData.auth) ? '1' : '0');

        // enable ccpa privacy based on region and one trust target Cookies disabled
        if (hasUserConsentLib() && isCCPA() && !inUserConsentState(['ads-general', 'behavior-general', 'perf-general'])) {
            this.theContext.addKeyValue('_fw_us_privacy', '1NYN');
        }

        this.theContext.submitRequest();

        return this.theContext;
    }


    setContextVideoState (state) {
        this.theContext.setVideoState(state);
    }


    setMVPD (mvpd) {
        const md5MVPD = md5(mvpd.toLowerCase());
        this.mvpd = md5MVPD;

        if (this.theContext) {
            this.theContext.addKeyValue('_fw_ae', this.mvpd);
        }
    }


    setSlots () {
        this.prerolls = [];
        this.midrolls = [];
        this.postrolls = [];

        var fwTemporalSlots = this.theContext.getTemporalSlots();
        for (var i = 0; i < fwTemporalSlots.length; i++) {
            var slot = fwTemporalSlots[i];
            switch (slot.getTimePositionClass()) {
                case tv.freewheel.SDK.TIME_POSITION_CLASS_PREROLL:
                    if (this.showPreroll) this.prerolls.push(slot);
                    break;
                case tv.freewheel.SDK.TIME_POSITION_CLASS_MIDROLL:
                // case tv.freewheel.SDK.TIME_POSITION_CLASS_OVERLAY:
                    if (this.showMidroll) this.midrolls.push(slot);
                    break;
                case tv.freewheel.SDK.TIME_POSITION_CLASS_POSTROLL:
                    if (this.showPostroll) this.postrolls.push(slot);
                    break;
            }
        }

        // sort midrolls
        this.midrolls.sort(function(x, y) { return x.getTimePosition() > y.getTimePosition(); } );
    }


    getMidroll (currentTime) {
        const validMidrolls = [];
        for (let i = 0; i < this.midrolls.length; i++) {
            const slot = this.midrolls[i];
            const slotTimePosition = slot.getTimePosition();

            if ((currentTime - slotTimePosition >= 0)) validMidrolls.push(slot);
        }

        if (validMidrolls.length) {
            // remove the midroll from the main array
            this.midrolls.splice(0, validMidrolls.length);
            // only play the last midroll in the group (in case the user seeked ahead past more than one midroll)
            return validMidrolls[validMidrolls.length - 1];
        } else return null;
    }


    startPreroll (slot) {
        slot = (slot) ? slot : this.prerolls[0];

        this.currentPlayingSlot = slot;
        this.currentPlayingSlotType = slot.getTimePositionClass();
        slot.play();
    }


    startMidroll (slot) {
        slot = (slot) ? slot : this.midrolls[0];

        this.currentPlayingSlot = slot;
        this.currentPlayingSlotType = slot.getTimePositionClass();
        slot.play();
    }


    startPostroll (slot) {
        slot = (slot) ? slot : this.postrolls[0];

        this.currentPlayingSlot = slot;
        this.currentPlayingSlotType = slot.getTimePositionClass();
        slot.play();
    }


    configureShortcuts () {
        var isCtrl = false;

        var onKeyDown = (e) => {
            // ctrl+s - skip current ad
            if (e.keyCode == 17) isCtrl = true;
            if (e.keyCode == 83 && isCtrl == true) {
                this.skipAd();
            }
        };

        var onKeyUp = (e) => {
            if (e.keyCode == 17) isCtrl = false;
        };

        document.onkeydown = onKeyDown;
        document.onkeyup = onKeyUp;
    }


    skipAd () {
        if (this.currentPlayingSlot) this.currentPlayingSlot.skipCurrentAd();
    }


    getCurrentSlot () {
        return this.currentPlayingSlot;
    }
}


export default Freewheel;



// WEBPACK FOOTER //
// ./src/ads/freewheel/index.js